import { Button } from 'components/Button/Button';
import { useState } from 'react';
import type { FC } from 'react';
import { MultiplesGenerator } from './MultiplesGenerator/components/MultiplesGenerator';

export const CommandModelGallerySection: FC = () => {
  // demonstrate reactivity of outer state of the command model
  const [baseNumber, setBaseNumber] = useState(1);
  return (
    <div className='max-w-screen-sm'>
      <p>
        This is a technical demo of a state management technique, not any
        particular visual component.
      </p>
      <h3>Example component managed by command model</h3>
      <MultiplesGenerator baseNumber={baseNumber} />
      <p>
        Alter base number:{' '}
        <Button
          variant='secondary'
          className='inline'
          onClick={() => {
            setBaseNumber(baseNumber - 1);
          }}
          icon='fa-minus'
          type='button'
        />
        <Button
          variant='secondary'
          className='inline'
          icon='fa-plus'
          onClick={() => {
            setBaseNumber(baseNumber + 1);
          }}
          type='button'
        />
      </p>
      <h3>What it is</h3>
      <p>
        We want to avoid using redux mainly for being too heavyweight
        (ultraheavy with typescript), but still we have to manage state somehow
        and our architecture demands are far beyond what you can do with
        useState/useReducer hooks.
      </p>
      <p>
        For that reason we introduced our own state management approach, that is
        somewhere between global state management and lightweight local state.
      </p>
      <h3>The model is composed of these components:</h3>
      <ul>
        <li>useStateProxy</li>
        <li>useCommandDispatcher</li>
        <li>SelectorCache</li>
        <li>+ documented easy integration of context selectors</li>
      </ul>
      <h3>Advantages over Redux</h3>
      <ul>
        <li>
          The state (and also all memoized stuff) is scoped by the main hook, so
          you can simply have multiple instances of the component on the page
          without the need to solve the architecture of managing multiple
          instances in one global state object (this is pain in redux and not
          resolved by reselect selectors at all).
        </li>
        <li>
          Commands are way simpler - there are no &quot;command objects sent to
          a global reduce function&quot;. When you want a new command, you
          basically write a new function (a bit special, see examples in{' '}
          <code>MultiplesGenerator/model/commands</code> ) and call it via our{' '}
          <code>dispatch</code> where you need. No need to write a structure of
          reducers, manage types of action objects etc. Long story short,{' '}
          <strong>far less</strong> boilerplate.
        </li>
        <li>
          There are no sagas. But thanks to some theoretical compromises, it is
          really simple to create a command that just calls other commands and
          use ES8&apos;s async/await to deal with asynchronous
          &quot;sagas&quot;, so you can write saga-like code just like a
          javascript function without studying not-so-trivial libraries.
        </li>
      </ul>
      <h3>Disadvantages</h3>
      <ul>
        <li>
          There are no monitoring tools like redux devtools for our library. We
          have to live with console-log debugging or inspect the state by other
          means.
        </li>
        <li>
          It is not clearly resolved yet how multiple models can communicate
          between each other. There is an egg/chicken problem - when you
          instantiate model A first and model B then, it needs some trickery to
          make model A see model B.
        </li>
      </ul>
      <hr />
    </div>
  );
};
